/**
 * TraceView Component
 *
 * Unified execution trace view with progressive disclosure.
 * Replaces the tabbed Dashboard/Tree/Timeline structure.
 *
 * Features:
 * - Sticky header with status, progress, stats
 * - Filter chips for activity types
 * - Collapsible steps with nested activities
 * - Auto-grouping by: explicit steps, agent, or LLM turns
 */

import { Component, createSignal, createMemo, Show, For } from 'solid-js';
import type { ExecutionEvent } from '../../../services/api.ts';
import type { AgentInfo, PlanStep, ExecutionStats, ExecutionProgress, ExecutionPhase } from '../../../stores/executionStore.ts';
import TraceHeader from './TraceHeader.tsx';
import TraceFilters, { type ActivityFilter } from './TraceFilters.tsx';
import TraceStep, { type TraceStepData, type StepStatus } from './TraceStep.tsx';
import TraceActivity from './TraceActivity.tsx';

export interface TraceViewProps {
  events: ExecutionEvent[];
  agents: AgentInfo[];
  planSteps: PlanStep[];
  stats: ExecutionStats;
  progress: ExecutionProgress;
  phase: ExecutionPhase;
  status: string;
  agentName?: string;
  isLive: boolean;
  selectedAgentId: string | null;
  onAgentSelect: (agentId: string | null) => void;
  formatTime: (timestamp: string) => string;
  formatDuration: (ms?: number) => string;
  error?: string;
}

// Group events by explicit plan steps
function groupByPlanSteps(events: ExecutionEvent[], planSteps: PlanStep[]): TraceStepData[] {
  if (planSteps.length === 0) return [];

  const stepMap = new Map<string, TraceStepData>();

  // Initialize steps from plan
  planSteps.forEach((step, index) => {
    stepMap.set(step.id, {
      id: step.id,
      index,
      description: step.description,
      status: step.status === 'in_progress' ? 'running' :
              step.status === 'completed' ? 'completed' :
              step.status === 'failed' ? 'failed' : 'pending',
      activities: [],
      durationMs: undefined,
    });
  });

  // Track current step for activity assignment
  let currentStepId: string | null = null;
  let stepStartTime: Date | null = null;

  events.forEach(event => {
    const payload = event.payload as Record<string, unknown>;

    // Track step transitions
    if (event.eventType === 'step_started') {
      currentStepId = payload?.stepId as string || null;
      stepStartTime = new Date(event.timestamp);
      if (currentStepId && stepMap.has(currentStepId)) {
        stepMap.get(currentStepId)!.status = 'running';
      }
    } else if (event.eventType === 'step_completed' || event.eventType === 'step_failed') {
      const stepId = payload?.stepId as string || currentStepId;
      if (stepId && stepMap.has(stepId)) {
        const step = stepMap.get(stepId)!;
        step.status = event.eventType === 'step_completed' ? 'completed' : 'failed';
        if (stepStartTime) {
          step.durationMs = new Date(event.timestamp).getTime() - stepStartTime.getTime();
        }
      }
      currentStepId = null;
      stepStartTime = null;
    }

    // Assign activity events to current step
    if (currentStepId && stepMap.has(currentStepId)) {
      const isActivityEvent =
        event.eventType.startsWith('tool_call') ||
        event.eventType.startsWith('llm_call') ||
        event.eventType.startsWith('thinking') ||
        event.eventType === 'agent_message';

      if (isActivityEvent) {
        // Only add completed events to avoid duplicates from start/complete pairs
        if (!event.eventType.includes('started')) {
          stepMap.get(currentStepId)!.activities.push(event);
        }
      }
    }
  });

  return Array.from(stepMap.values());
}

// Group events by LLM turns (each LLM call + preceding tools)
function groupByLLMTurns(events: ExecutionEvent[]): TraceStepData[] {
  const turns: TraceStepData[] = [];
  let currentTurn: TraceStepData | null = null;
  let turnIndex = 0;
  let turnStartTime: Date | null = null;

  // Filter to only activity-relevant events
  const activityEvents = events.filter(e =>
    e.eventType.startsWith('tool_call') ||
    e.eventType.startsWith('llm_call') ||
    e.eventType.startsWith('thinking') ||
    e.eventType === 'agent_message'
  );

  activityEvents.forEach(event => {
    // Start new turn on LLM call start
    if (event.eventType === 'llm_call_started') {
      if (currentTurn) {
        // Close previous turn
        if (turnStartTime) {
          currentTurn.durationMs = new Date(event.timestamp).getTime() - turnStartTime.getTime();
        }
        turns.push(currentTurn);
      }
      turnIndex++;
      turnStartTime = new Date(event.timestamp);
      currentTurn = {
        id: `turn-${turnIndex}`,
        index: turnIndex - 1,
        description: `Processing turn ${turnIndex}`,
        status: 'running',
        activities: [],
      };
    }

    // Add activity to current turn (skip 'started' events to avoid duplicates)
    if (currentTurn && !event.eventType.includes('started')) {
      currentTurn.activities.push(event);

      // Update description based on activities
      const toolNames = currentTurn.activities
        .filter(e => e.eventType === 'tool_call_completed')
        .map(e => (e.payload as Record<string, unknown>)?.toolName as string)
        .filter(Boolean);

      if (toolNames.length > 0) {
        currentTurn.description = `Tools: ${toolNames.slice(0, 3).join(', ')}${toolNames.length > 3 ? '...' : ''}`;
      }

      // Mark as completed on LLM completion
      if (event.eventType === 'llm_call_completed') {
        currentTurn.status = 'completed';
      }

      // Mark as failed on any failure
      if (event.eventType.includes('failed')) {
        currentTurn.status = 'failed';
      }
    }
  });

  // Push final turn
  if (currentTurn) {
    turns.push(currentTurn);
  }

  return turns;
}

// Group ungrouped events (those not in steps or turns)
function getUngroupedEvents(events: ExecutionEvent[], steps: TraceStepData[]): ExecutionEvent[] {
  const groupedEventIds = new Set<string>();

  steps.forEach(step => {
    step.activities.forEach(activity => {
      groupedEventIds.add(activity.id);
    });
  });

  return events.filter(event => {
    const isActivityEvent =
      event.eventType.startsWith('tool_call') ||
      event.eventType.startsWith('llm_call') ||
      event.eventType.startsWith('thinking') ||
      event.eventType === 'agent_message';

    // Only show completed events to avoid duplicates
    const isCompletedEvent = !event.eventType.includes('started');

    return isActivityEvent && isCompletedEvent && !groupedEventIds.has(event.id);
  });
}

const TraceView: Component<TraceViewProps> = (props) => {
  const [activityFilter, setActivityFilter] = createSignal<ActivityFilter>('all');
  const [searchQuery, setSearchQuery] = createSignal('');

  // Count activities by type
  const activityCounts = createMemo(() => {
    const events = props.events;
    return {
      all: events.length,
      thinking: events.filter(e => e.eventType.startsWith('thinking')).length,
      tools: events.filter(e => e.eventType.startsWith('tool_call')).length,
      llm: events.filter(e => e.eventType.startsWith('llm_call')).length,
      errors: events.filter(e => e.eventType.includes('failed')).length,
    };
  });

  // Filter events based on current filter
  const filteredEvents = createMemo(() => {
    let events = props.events;
    const filter = activityFilter();
    const query = searchQuery().toLowerCase();

    // Apply type filter
    if (filter !== 'all') {
      events = events.filter(e => {
        switch (filter) {
          case 'thinking':
            return e.eventType.startsWith('thinking');
          case 'tools':
            return e.eventType.startsWith('tool_call');
          case 'llm':
            return e.eventType.startsWith('llm_call');
          case 'errors':
            return e.eventType.includes('failed');
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (query) {
      events = events.filter(e => {
        const payload = e.payload as Record<string, unknown>;
        const searchableText = JSON.stringify(payload).toLowerCase();
        return searchableText.includes(query) ||
               e.eventType.toLowerCase().includes(query) ||
               (e.agentName?.toLowerCase().includes(query) ?? false);
      });
    }

    return events;
  });

  // Group events into steps
  const groupedSteps = createMemo((): TraceStepData[] => {
    const events = filteredEvents();

    // Priority 1: Use explicit plan steps if available
    if (props.planSteps.length > 0) {
      return groupByPlanSteps(events, props.planSteps);
    }

    // Priority 2: Group by LLM turns
    return groupByLLMTurns(events);
  });

  // Get any ungrouped activities (for display before steps or at the end)
  const ungroupedActivities = createMemo(() => {
    return getUngroupedEvents(filteredEvents(), groupedSteps());
  });

  // Check if we have any content to display
  const hasContent = createMemo(() => {
    return groupedSteps().length > 0 || ungroupedActivities().length > 0;
  });

  return (
    <div class="space-y-4">
      {/* Header */}
      <TraceHeader
        agentName={props.agentName}
        status={props.status}
        phase={props.phase}
        progress={props.progress}
        stats={props.stats}
        isLive={props.isLive}
        formatDuration={props.formatDuration}
      />

      {/* Filters */}
      <TraceFilters
        activeFilter={activityFilter()}
        onFilterChange={setActivityFilter}
        searchQuery={searchQuery()}
        onSearchChange={setSearchQuery}
        agents={props.agents}
        selectedAgentId={props.selectedAgentId}
        onAgentSelect={props.onAgentSelect}
        counts={activityCounts()}
      />

      {/* Error display */}
      <Show when={props.error}>
        <div class="bg-red-500/10 rounded-lg border border-red-500/30 p-4">
          <h3 class="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Error
          </h3>
          <pre class="text-xs text-red-300 whitespace-pre-wrap overflow-x-auto">
            {props.error}
          </pre>
        </div>
      </Show>

      {/* Main trace content */}
      <div class="space-y-3">
        {/* Ungrouped activities at the start (planning phase, etc.) */}
        <Show when={ungroupedActivities().length > 0 && groupedSteps().length > 0}>
          <div class="bg-gray-800/30 rounded-lg border border-gray-700 p-4">
            <h3 class="text-sm font-medium text-gray-400 mb-3">Preparation</h3>
            <div class="space-y-1">
              <For each={ungroupedActivities().slice(0, 5)}>
                {(event) => (
                  <TraceActivity
                    event={event}
                    agents={props.agents}
                    formatTime={props.formatTime}
                    formatDuration={props.formatDuration}
                  />
                )}
              </For>
              <Show when={ungroupedActivities().length > 5}>
                <div class="text-xs text-gray-500 pl-7 py-1">
                  + {ungroupedActivities().length - 5} more activities
                </div>
              </Show>
            </div>
          </div>
        </Show>

        {/* Steps */}
        <For each={groupedSteps()}>
          {(step) => (
            <TraceStep
              step={step}
              agents={props.agents}
              formatTime={props.formatTime}
              formatDuration={props.formatDuration}
              defaultExpanded={step.status === 'running'}
            />
          )}
        </For>

        {/* Ungrouped activities when no steps exist */}
        <Show when={ungroupedActivities().length > 0 && groupedSteps().length === 0}>
          <div class="bg-gray-800/30 rounded-lg border border-gray-700 p-4">
            <h3 class="text-sm font-medium text-gray-400 mb-3">Activities</h3>
            <div class="space-y-1">
              <For each={ungroupedActivities()}>
                {(event) => (
                  <TraceActivity
                    event={event}
                    agents={props.agents}
                    formatTime={props.formatTime}
                    formatDuration={props.formatDuration}
                  />
                )}
              </For>
            </div>
          </div>
        </Show>

        {/* Empty state */}
        <Show when={!hasContent() && props.events.length === 0}>
          <div class="bg-gray-800/30 rounded-lg border border-gray-700 p-8 text-center">
            <Show when={props.status === 'running' || props.status === 'pending'}>
              <div class="flex items-center justify-center gap-3 text-gray-400">
                <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Waiting for events...</span>
              </div>
            </Show>
            <Show when={props.status !== 'running' && props.status !== 'pending'}>
              <span class="text-gray-500">No events recorded</span>
            </Show>
          </div>
        </Show>

        {/* No results from filter */}
        <Show when={!hasContent() && props.events.length > 0}>
          <div class="bg-gray-800/30 rounded-lg border border-gray-700 p-8 text-center">
            <span class="text-gray-500">No activities match your filters</span>
            <button
              type="button"
              onClick={() => {
                setActivityFilter('all');
                setSearchQuery('');
              }}
              class="block mx-auto mt-2 text-sm text-purple-400 hover:text-purple-300"
            >
              Clear filters
            </button>
          </div>
        </Show>
      </div>
    </div>
  );
};

export default TraceView;
export { TraceHeader, TraceFilters, TraceStep, TraceActivity };
export type { ActivityFilter, TraceStepData, StepStatus };
